import { useEditor, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Speaker } from "src/types";
import { useBetween } from "use-between";

import PrimitivesFloatingOptions from "@/components/editor/block-outline/floating-options/primitives-floating-component";
import { SpeakerDetails } from "@/components/editor/shared/speakers/speaker-bio/speaker-details-component";
import {
  ActionText,
  HeadlineField,
  NameField,
  ZoomCard,
} from "@/components/editor/shared/speakers/speaker-bio/speaker-details-styles";
import { SpeakerModal } from "@/components/editor/shared/speakers/speaker-bio/speaker-modal";
import SyncedSpeakersSettings from "@/components/editor/shared/speakers/speakers-settings/synced-speakers-settings-component";
import {
  getSocialLinkAriaLabel,
  useSpeakerModal,
} from "@/components/editor/shared/speakers/utils";
import useTheme from "@/components/editor/themes/use-theme";
import {
  ColorLabels,
  getColorByLabel,
} from "@/components/editor/utils/use-color";
import { LinkedInSquare } from "@/components/icons/linkedin-square";
import { Twitter } from "@/components/icons/twitter";
import { Website } from "@/components/icons/website";
import useAnalytics from "@/hooks/use-analytics";
import useProperties from "@/hooks/use-properties";
import { isAttendeesPage } from "@/utils/is-attendees-page";

import type { SpeakerLayout } from "../synced-speakers-component";
import { Card, IconLinkWrapper } from "./speaker-card-component-styles";

export type SyncedSpeakerCardProps = {
  speaker: Speaker;
  layout?: SpeakerLayout;
  isBioAvailable?: boolean;
};

const SPEAKER_NAME_ID = "speaker-name";
const SPEAKER_HEADLINE_ID = "speaker-headline";

export default function SyncedSpeakerCard({
  speaker,
  layout = "large-cards",
  isBioAvailable,
}: SyncedSpeakerCardProps) {
  const {
    connectors: { connect },
  } = useNode((node) => ({
    linkedNodes: node.getLinkedNodes(),
    nodeId: node.id,
  }));
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const { speaker: activeSpeaker, setSpeaker } = useBetween(useSpeakerModal);
  const { logAnalytics } = useAnalytics();
  const { enabled } = useEditor((state) => ({
    enabled: state.isEnabled(),
  }));
  const backgroundColor = getColorByLabel(ColorLabels.BACKGROUND);
  const visualsColor = getColorByLabel(ColorLabels.VISUALS);
  const speakerName = `${speaker.first_name} ${speaker.last_name}`;

  const style = React.useMemo(() => {
    if (layout === "large-cards") {
      return getLargeCardStyles(backgroundColor, visualsColor);
    }
  }, [layout, backgroundColor, visualsColor]);

  const isActionable = React.useMemo(
    () => isBioAvailable && (isAttendeesPage() || !enabled),
    [enabled, isBioAvailable]
  );

  const handleClick = () => {
    if (isAttendeesPage()) {
      logAnalytics("CANVAS_SPEAKER_CARD_CLICKED", {
        is_bio_enabled: isBioAvailable,
      });
    }

    if (!isAttendeesPage() && enabled) {
      return null;
    }

    setSpeaker(speaker);
  };

  const properties = useProperties();

  const iconStyle = React.useMemo(() => {
    return {
      color: properties.colors[ColorLabels.PRIMARY],
    };
  }, [properties.colors]);

  return (
    <div style={style.containerStyle} data-testid="speaker-card" ref={connect}>
      {layout === "large-cards" && (
        <Card>
          <ZoomCard
            className={currentTheme.kind}
            actionable={isActionable}
            onClick={handleClick}
          >
            <img
              data-testid="speaker-profile-picture"
              draggable="false"
              src={speaker.image_url}
              alt={speaker.name}
              style={style.imageStyle}
            />
          </ZoomCard>
          <ActionText
            className={currentTheme.kind}
            actionable={isActionable}
            onClick={handleClick}
          >
            <NameField
              data-testid="speaker-name-field"
              id={SPEAKER_NAME_ID}
              className="text-primitive headingFour"
              style={{ fontWeight: "600" }}
            >
              {`${speaker.first_name} ${speaker.last_name}`}
            </NameField>
          </ActionText>
          <HeadlineField
            id={SPEAKER_HEADLINE_ID}
            data-testid="speaker-headline-field"
            style={{ textAlign: "center" }}
          >
            {speaker.headline}
          </HeadlineField>
          <div style={style.socialLinks}>
            {speaker.social?.linkedin_url && (
              <IconLinkWrapper
                href={speaker.social.linkedin_url}
                target="_blank"
                rel="noreferrer"
                aria-label={getSocialLinkAriaLabel(speakerName, "LinkedIn", t)}
              >
                <LinkedInSquare
                  className="size-[18px]"
                  style={iconStyle}
                  aria-label="LinkedIn"
                />
              </IconLinkWrapper>
            )}
            {speaker.social?.twitter_url && (
              <IconLinkWrapper
                href={speaker.social.twitter_url}
                target="_blank"
                rel="noreferrer"
                aria-label={getSocialLinkAriaLabel(speakerName, "Twitter", t)}
              >
                <Twitter
                  className="size-[18px]"
                  style={iconStyle}
                  aria-label="Twitter"
                />
              </IconLinkWrapper>
            )}
            {speaker.social?.website_url && (
              <IconLinkWrapper
                href={speaker.social.website_url}
                target="_blank"
                rel="noreferrer"
                aria-label={getSocialLinkAriaLabel(speakerName, "Website", t)}
              >
                <Website
                  className="size-[18px]"
                  style={iconStyle}
                  aria-label="Website"
                />
              </IconLinkWrapper>
            )}
          </div>
        </Card>
      )}
      <SpeakerModal
        isShowing={activeSpeaker?.id === speaker.id}
        onClose={() => setSpeaker(null)}
        isDismissible
        className={currentTheme.kind}
      >
        <SpeakerDetails
          name={`${speaker.first_name} ${speaker.last_name}`}
          headline={speaker.headline}
          bio={speaker.bio}
          social={speaker.social}
          image_url={speaker.image_url}
        />
      </SpeakerModal>
    </div>
  );
}

const getLargeCardStyles = (backgroundColor: string, visualsColor: string) => {
  return {
    containerStyle: {
      width: "100%",
      maxWidth: rem(282),
      margin: `0 ${rem(12)} ${rem(24)} ${rem(12)}`,
      border: `${rem(2)} solid ${visualsColor}`,
      backgroundColor,
      padding: rem(24),
    },
    socialLinks: {
      display: "grid",
      gridAutoFlow: "column",
      gridAutoColumns: "max-content",
      margin: "auto",
      gap: rem(16),
    },
    imageStyle: {
      display: "block",
      height: rem(128),
      width: rem(128),
      objectFit: "cover",
      borderRadius: "50%",
    } as React.CSSProperties,
  };
};

SyncedSpeakerCard.craft = {
  custom: {
    displayName: "Synced Speaker Card",
  },
  related: {
    leftOutlineOptions: PrimitivesFloatingOptions,
    settings: SyncedSpeakersSettings,
  },
};
